import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CandidatesPage from './pages/CandidatesPage';
import TasksPage from './pages/TasksPage';
import SettingsPage from './pages/SettingsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import CVBuilderPage from './pages/CVBuilderPage';
import ReceptionistViewPage from './pages/ReceptionistViewPage';
import AssociatesPage from './pages/AssociatesPage';
import JobsPage from './pages/JobsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import JobGeneratorPage from './pages/JobGeneratorPage';
import ReportsPage from './pages/ReportsPage';
import DocumentsPage from './pages/DocumentsPage';
import RecycleBinPage from './pages/RecycleBinPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  
  
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* 🚀 END DEVELOPMENT BYPASS */}
      
      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates"
        element={
          <ProtectedRoute>
            <Layout>
              <CandidatesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Layout>
              <TasksPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <ProtectedRoute>
            <Layout>
              <WhatsAppPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cv-builder"
        element={
          <ProtectedRoute>
            <Layout>
              <CVBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/receptionist-view"
        element={
          <ProtectedRoute>
            <Layout>
              <ReceptionistViewPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/associates"
        element={
          <ProtectedRoute>
            <Layout>
              <AssociatesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <ProtectedRoute>
            <Layout>
              <JobsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <Layout>
              <AppointmentsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/job-generator"
        element={
          <ProtectedRoute>
            <Layout>
              <JobGeneratorPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <ReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Layout>
              <DocumentsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recycle-bin"
        element={
          <ProtectedRoute>
            <Layout>
              <RecycleBinPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Add more routes as needed */}
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SettingsProvider>
          <NotificationProvider>
            <Router>
              <AppContent />
            </Router>
          </NotificationProvider>
        </SettingsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

const AppContent: React.FC = () => {
  const { settings } = useSettings();

  React.useEffect(() => {
    // Temporary debug function to catch updatePatientInfo calls
    (window as any).updatePatientInfo = (...args: any[]) => {
      console.error('updatePatientInfo was called with:', args);
      console.trace('Call stack:');
      // Instead of alerting, just log and return empty result
      return {};
    };
    
    // Check localStorage for any problematic data
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value && value.includes('updatePatientInfo')) {
          console.warn(`Found 'updatePatientInfo' in localStorage key: ${key}`);
          console.warn('Value:', value);
        }
      });
    } catch (e) {
      console.error('Error checking localStorage:', e);
    }
  }, []);

  React.useEffect(() => {
    const root = window.document.documentElement;
    if (settings.app.darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [settings.app.darkMode]);

  return (
    <div className="App">
      <AppRoutes />
    </div>
  );
};

export default App;
